import { StationList } from '@/api/bsaMap';

export interface StationPoint {
  id: number;
  longitude: number;
  latitude: number;
}

interface StationCluster {
  longitude: number;
  latitude: number;
  points: StationPoint[];
}

const TILE_SIZE = 256;
const GRID_SIZE = 80;
const MAX_CLUSTER_ZOOM = 7;

export const toStationPoints = (stations?: StationList[]): StationPoint[] =>
  (stations ?? []).flatMap(station => {
    const longitude = Number(station.longitude);
    const latitude = Number(station.latitude);
    if (!Number.isFinite(longitude) || !Number.isFinite(latitude)) return [];
    if (longitude < -180 || longitude > 180 || latitude < -85 || latitude > 85) return [];
    return [{ id: station.id, longitude, latitude }];
  });

const projectToWorldPixel = (point: StationPoint, zoom: number) => {
  const scale = TILE_SIZE * 2 ** zoom;
  const sinLatitude = Math.sin((point.latitude * Math.PI) / 180);
  return {
    x: ((point.longitude + 180) / 360) * scale,
    y: (0.5 - Math.log((1 + sinLatitude) / (1 - sinLatitude)) / (4 * Math.PI)) * scale,
  };
};

export const clusterStations = (points: StationPoint[], zoom: number): StationCluster[] => {
  if (zoom > MAX_CLUSTER_ZOOM) {
    return points.map(point => ({ longitude: point.longitude, latitude: point.latitude, points: [point] }));
  }

  const buckets = new Map<string, StationPoint[]>();
  points.forEach(point => {
    const pixel = projectToWorldPixel(point, zoom);
    const key = `${Math.floor(pixel.x / GRID_SIZE)}:${Math.floor(pixel.y / GRID_SIZE)}`;
    buckets.set(key, [...(buckets.get(key) ?? []), point]);
  });

  return Array.from(buckets.values()).flatMap(bucket => {
    if (bucket.length < 3) {
      return bucket.map(point => ({ longitude: point.longitude, latitude: point.latitude, points: [point] }));
    }
    return [
      {
        longitude: bucket.reduce((sum, point) => sum + point.longitude, 0) / bucket.length,
        latitude: bucket.reduce((sum, point) => sum + point.latitude, 0) / bucket.length,
        points: bucket,
      },
    ];
  });
};

const svgToDataUrl = (svg: string) => `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;

export const createStationIcon = (selected: boolean) =>
  svgToDataUrl(`
    <svg xmlns="http://www.w3.org/2000/svg" width="34" height="46" viewBox="0 0 34 46">
      <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
        <feDropShadow dx="0" dy="2" stdDeviation="2" flood-color="#000" flood-opacity=".45"/>
      </filter>
      <path filter="url(#shadow)" fill="${selected ? '#e64242' : '#1677ff'}" stroke="#fff" stroke-width="2"
        d="M17 1C8.2 1 1 8.2 1 17c0 12 16 28 16 28s16-16 16-28C33 8.2 25.8 1 17 1z"/>
      <circle cx="17" cy="17" r="6" fill="#fff"/>
    </svg>`);

export const getClusterStyle = (count: number) => {
  const digits = `${count}`.length;
  const size = digits === 1 ? 48 : digits === 2 ? 60 : 80;
  return {
    size,
    className: `size-mode-${Math.min(digits, 5)}`,
  };
};
